import { FC, useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import { IonCardContent, IonContent, IonLabel, IonSegmentButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { ICaremapNote } from '../model/NotesClient';
import { createUseStyles } from 'react-jss';
import { EnvelopeOutlineIcon } from '../../theme/Icons';
import { Reaction } from '../reactions/Reaction';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { IOSTabs } from '../../components/IOSTabs';
import { INoteInteractionSurface } from '../reactions/INoteInteractionSurface';

const useStyles = createUseStyles({
    content: {
        paddingTop: 0
    },

    row: {
        gap: 8,
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center'
    },

    segmentWrapper: {
        marginBottom: 18
    },

    information: {
        paddingLeft: 8,
        paddingRight: 8
    }
});

export type INoteInteractionInfoType = 'notifications' | 'reactions';
export type INoteInteractionModalProps = {
    closeHandler: () => void;
    surface: INoteInteractionSurface;
    modalInfo?: {
        note: ICaremapNote;
        type: INoteInteractionInfoType;
    }
};

// What happened to a note: who reacted, and who was sent it.
export const NoteInteractionModal: FC<INoteInteractionModalProps> = ({ modalInfo, surface, closeHandler = () => {} }) => {
    const classes = useStyles();

    const [infoType, setInfoType] = useState<INoteInteractionInfoType>(modalInfo?.type || 'reactions');

    const mixpanel = useInstance(MixPanelEventEmitter);
    const reactionsModel = useInstance(CaremapNotesModelFactory).forActiveCaremap().reactions;
    const possibleReactions = reactionsModel.getSupportedReactions();

    useEffect(() => {
        if (modalInfo) {
            setInfoType(modalInfo.type);
        }
    }, [modalInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (modalInfo) {
            if (modalInfo.type === 'reactions') {
                let count = 0;
                possibleReactions.forEach(reaction => {
                    count += reactionsModel.getNames({ note: modalInfo.note, reaction }).length;
                });

                mixpanel.track('Reactions Modal Opened', () => ({
                    '# of Reactions': count,
                    'Surface': surface
                }));
            } else {
                mixpanel.track('Sent To Modal Opened', () => ({
                    '# of Notifications Sent': reactionsModel.getNames({ note: modalInfo.note, reaction: 'notification-sent:email' }).length,
                    'Surface': surface
                }));
            }
        }
    }, [modalInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleType = (type: INoteInteractionInfoType) => {
        if (infoType !== 'reactions' && type === 'reactions') {
            mixpanel.track('Toggled Reactions / Sent Modal', () => ({
                from: 'Sent To',
                to: 'Reactions',
                'Surface': surface
            }));
        } else {
            mixpanel.track('Toggled Reactions / Sent Modal', () => ({
                from: 'Reactions',
                to: 'Sent To',
                'Surface': surface
            }));
        }
        setInfoType(type);
    };

    return (
        <Modal
            desktopMaxWidth={500}
            desktopMaxHeight={400}
            data-id="note-interaction-modal"
            closeHandler={closeHandler}
            isOpen={Boolean(modalInfo)}
            title="Note Interaction"
        >
            <IonContent>
                <IonCardContent className={classes.content}>
                    <div className={classes.segmentWrapper}>
                        {/* if user doesn't have access to Sent To, hide toggle */}
                        {modalInfo && reactionsModel.getNames({ note: modalInfo.note, reaction: 'notification-sent:email' }).length ? (
                            <IOSTabs data-id="note-interaction-toggle" value={infoType}>
                                <IonSegmentButton value="reactions" onClick={() => toggleType('reactions')}>
                                    <IonLabel>Reactions</IonLabel>
                                </IonSegmentButton>
                                <IonSegmentButton value="notifications" onClick={() => toggleType('notifications')}>
                                    <IonLabel>Sent To</IonLabel>
                                </IonSegmentButton>
                            </IOSTabs>
                        ) : null}
                    </div>
                    <div className={classes.information}>
                        {modalInfo && infoType === 'reactions' ? (
                            <div data-id="reactions">
                                {possibleReactions.map(reaction => (
                                    reactionsModel.getNames({ note: modalInfo.note, reaction }).map((name, idx) => (
                                        <div key={idx} className={classes.row}>
                                            <Reaction reaction={reaction} hasReaction />
                                            <div className="mp-mask">{name}</div>
                                        </div>
                                    ))
                                ))}
                            </div>
                        ) : (
                            modalInfo && (
                                <div data-id="notifications">
                                    {reactionsModel.getNames({ note: modalInfo.note, reaction: 'notification-sent:email' }).map((name, idx) => {
                                        const names = name.split(' ');
                                        const fullName = names.length > 2 ? `${names[0]} ${names[1]}` : names[0];
                                        const email = names.length > 2 ? names[2] : names[1];
                                        return (
                                            <div key={idx} className={classes.row} style={{alignItems: 'flex-start'}}>
                                                <div><EnvelopeOutlineIcon /></div>
                                                <div className="mp-mask">
                                                    <div>{fullName.replaceAll('"', '')}</div>
                                                    <div>{email.replaceAll('<', '').replaceAll('>', '')}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )
                        )}
                    </div>
                </IonCardContent>
            </IonContent>
        </Modal>
    );
};
